import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, ExternalLink, ChevronDown } from "lucide-react";
import { NAV, PARTNER_LINKS, BUSINESS, CTA } from "../data/site";
import { IMAGES } from "../mock/mock";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? "bg-[#0b0b0b]/95 backdrop-blur-md py-2 shadow-[0_4px_30px_rgba(0,0,0,0.6)] border-b border-[#c9a24b]/20" : "bg-gradient-to-b from-black/70 to-transparent py-3"}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Build Highland Homes logo" className={`transition-all duration-500 rounded-sm ${scrolled ? "h-11 w-11" : "h-12 w-12"} object-cover ring-1 ring-[#c9a24b]/40`} />
          <div className="text-left leading-tight">
            <span className="block font-serif-display text-lg md:text-xl tracking-wide gold-gradient-text font-semibold">Build Highland Homes</span>
            <span className="block text-[9px] md:text-[10px] tracking-[0.22em] uppercase text-[#a39d90]">Custom Home Builder · Central FL</span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV.map((item) => (
            <div key={item.to} className="relative group">
              <Link to={item.to} className="relative flex items-center gap-1 text-sm tracking-wide text-[#f3ede0] hover:text-[#e3c77a] transition-colors py-2">
                {item.label}
                {item.children && <ChevronDown size={13} />}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#c9a24b] transition-all duration-300 group-hover:w-full" />
              </Link>
              {item.children && (
                <div className="absolute left-0 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="bg-[#141414] border border-[#c9a24b]/20 min-w-[220px] py-2 shadow-xl">
                    {item.children.map((c) => (
                      <Link key={c.to} to={c.to} className="block px-5 py-2.5 text-sm text-[#cfc8b8] hover:text-[#e3c77a] hover:bg-white/5 transition-colors">{c.label}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
          {PARTNER_LINKS.map((p) => (
            <a key={p.href} href={p.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-sm tracking-wide text-[#a39d90] hover:text-[#e3c77a] transition-colors">
              {p.label}<ExternalLink size={12} />
            </a>
          ))}
          <Link to={CTA.primary.to} className="whitespace-nowrap border border-[#c9a24b] text-[#e3c77a] hover:bg-[#c9a24b] hover:text-[#0b0b0b] px-4 py-2.5 text-xs tracking-wide transition-all duration-300">Start Your Project</Link>
        </nav>

        <button className="xl:hidden text-[#e3c77a]" onClick={() => setOpen(!open)} aria-label="Toggle menu">
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      <div className={`xl:hidden overflow-hidden transition-all duration-500 bg-[#0b0b0b]/98 backdrop-blur-md ${open ? "max-h-[640px] border-t border-[#c9a24b]/20 overflow-y-auto" : "max-h-0"}`}>
        <div className="px-6 py-4 flex flex-col">
          {NAV.map((item) => (
            <div key={item.to}>
              <Link to={item.to} className="block py-3 text-[#f3ede0] hover:text-[#e3c77a] border-b border-white/5">{item.label}</Link>
              {item.children && (
                <div className="pl-4">
                  {item.children.map((c) => (
                    <Link key={c.to} to={c.to} className="block py-2 text-sm text-[#a39d90] hover:text-[#e3c77a]">{c.label}</Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {PARTNER_LINKS.map((p) => (
            <a key={p.href} href={p.href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 py-3 text-[#a39d90] hover:text-[#e3c77a] border-b border-white/5">{p.label}<ExternalLink size={13} /></a>
          ))}
          <Link to={CTA.primary.to} className="mt-3 flex items-center justify-center gap-2 bg-[#c9a24b] text-[#0b0b0b] px-5 py-3 text-sm tracking-wide font-medium">
            <Phone size={15} /> Start Your Custom Home Project
          </Link>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
